import React from 'react';

export type AuthUser = {
  email: string;
};

export type AuthContextType = {
  user: AuthUser | null;
  hasReportsAccess: boolean;
  login: (email: string) => void;
  logout: () => void;
};

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

/** Decode NCToken JWT payload and return FarmersUserType claim (no signature verification needed — UI only). */
function getFarmersUserTypeFromNCToken(): string {
  try {
    const ncToken = localStorage.getItem('NCToken') ?? '';
    if (!ncToken) return '';
    const payload = JSON.parse(atob(ncToken.split('.')[1]));
    return String(payload?.FarmersUserType ?? '');
  } catch {
    return '';
  }
}

async function fetchReportsAccess(): Promise<boolean> {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_COIPortal_Url ?? ''}/api/igigcoi/reports/access`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('igtoken') ?? ''}`,
          NCToken: localStorage.getItem('NCToken') ?? '',
        },
      }
    );
    if (!res.ok) return false;
    const data = await res.json();
    return data?.hasAccess === true;
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<AuthUser | null>(() => {
    try {
      const token = localStorage.getItem('igtoken');
      const storedUserName = localStorage.getItem('userName');
      if (token && storedUserName) return { email: storedUserName };
    } catch {}
    return null;
  });

  const [hasReportsAccess, setHasReportsAccess] = React.useState(false);

  // Resolve reports access once when a user session is active.
  // Allowed if: user is in coi_reports_configuration table  OR  NCToken FarmersUserType === "1"
  React.useEffect(() => {
    if (user) {
      const isFarmersAdmin = getFarmersUserTypeFromNCToken() === '1';
      if (isFarmersAdmin) {
        setHasReportsAccess(true);
      } else {
        fetchReportsAccess().then(setHasReportsAccess);
      }
    } else {
      setHasReportsAccess(false);
    }
  }, [user]);

  const login = (email: string) => {
    setUser({ email });
  };

  const logout = () => {
    setUser(null);
    setHasReportsAccess(false);
    try {
      localStorage.removeItem('igtoken');
      localStorage.removeItem('tenantId');
      localStorage.removeItem('userName');
      localStorage.removeItem('RoleId');
      localStorage.removeItem('AvatarName');
      localStorage.removeItem('st');
      localStorage.removeItem('SocialLogin');
      localStorage.removeItem('NCToken');
      localStorage.removeItem('NCAgencyId');
      localStorage.removeItem('NowcertsAgentId');
      localStorage.removeItem('ratetoken');
      localStorage.removeItem('firstName');
      localStorage.removeItem('lastName');
      localStorage.removeItem('workEmail');
    } catch {}
  };

  const value = React.useMemo(
    () => ({ user, hasReportsAccess, login, logout }),
    [user, hasReportsAccess]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
